import { useState } from "react";
import { toast } from "sonner";
import type { Stage } from "@salesboard/contracts";
import { useT } from "@/i18n";
import { ClosureDialog, ReopenDialog } from "./dialogs/MoveDialogs";
import { lastDayOfQuarter } from "@/lib/quarter";
import { formatQuarter } from "@/lib/format";
import { classifyDrop, needsDialog, type DropIntent, type DropTarget, type Movable } from "./drag/classifyDrop";
import { useCloseOpportunity, useMoveQuarter, useMoveStage, useReopenOpportunity } from "./hooks";

/** A move under way: awaiting confirmation, or in flight. Presentation state, never cache. */
export type PendingMove = { card: Movable; intent: DropIntent };

/**
 * The one place a move becomes a request.
 *
 * A drop and the detail's inline stage select produce the same thing — a target stage and
 * quarter — so both enter here, and the follow-up dialogs, the version guard and the pending
 * treatment are written once. That is what makes drag-free parity real rather than a second,
 * subtly different implementation.
 *
 * The target *picker* is gone (2026-08-20): the drag-free path is opening the card and
 * changing its stage or its date in place, which is every move the picker offered and one
 * fewer detour. The lifecycle dialogs it fronted are the product rules and remain.
 *
 * Nothing here patches the cache. While a move is pending the board renders that one card
 * **at its target cell** from `pending` (see `pendingPlacement`), and the cache continues to
 * hold committed server truth, so no count or total on screen is ever a guess. The pending
 * placement is released only when the refetch that follows the commit has landed — clearing
 * it any earlier puts the card back in its old cell for a frame, which is the spring-back
 * the book forbids. On failure there is nothing to spring back, because nothing ever claimed
 * to have moved.
 *
 * **One move at a time.** A second drop while a commit is in flight is refused rather than
 * replacing the first: a single pending slot with a shared clear would drop one card's
 * treatment and close the other card's dialog out from under the user.
 */
export function useMoveFlow({
  stages,
  boardYear,
}: {
  stages: Stage[] | undefined;
  /**
   * Undefined until the surface's own data has loaded. Deliberately not defaulted to the
   * browser's year: which year it is, is derived server-side like everything else, and a
   * fallback here would be a guess rendered as a fact. `begin` already refuses without
   * `stages`, so the flow is unreachable before both arrive.
   */
  boardYear: number | undefined;
}) {
  const t = useT();
  const [pending, setPending] = useState<PendingMove>();

  const moveStage = useMoveStage();
  const moveQuarter = useMoveQuarter();
  const close = useCloseOpportunity();
  const reopen = useReopenOpportunity();
  const committing = moveStage.isPending || moveQuarter.isPending || close.isPending || reopen.isPending;

  const clear = (): void => setPending(undefined);

  /**
   * Run the commit, then release the pending placement.
   *
   * `mutateAsync` resolves only after each mutation's `onSuccess` has awaited its
   * invalidation, so by the time this returns the board has re-read and the card is in its
   * new cell in the cache — the pending placement and committed truth agree, and releasing
   * one for the other is invisible.
   */
  const commit = async (
    run: () => Promise<unknown>,
    /**
     * What to say once it lands. Absent for the dialog paths, which report themselves — a
     * closure's own confirmation already named the outcome the reader chose.
     */
    landed?: { card: Movable; stageId?: string; quarter?: number },
  ): Promise<void> => {
    try {
      await run();
      // Only on success: the failure paths toast their own error with the diagnostic id, and
      // two toasts for one refused move would contradict each other.
      if (landed) toast.success(landedMessage(landed));
    } catch {
      // Swallowed deliberately. Every one of these mutations reports its own failure
      // through `onError` — the toast with the diagnostic identifier — and nothing here
      // branches on the outcome: the pending placement is released either way, which is
      // what puts the card back where it was.
      //
      // Without this, `mutateAsync` rethrows into a `void commit(...)` with no catch, and
      // a refused move becomes an unhandled promise rejection in the browser.
    } finally {
      clear();
    }
  };

  /**
   * Route a target. The dialog cases return without sending anything — which is what makes
   * "cancel writes nothing" a property of the shape of this function rather than a rule
   * someone has to remember.
   */
  const begin = (card: Movable, target: DropTarget): void => {
    if (!stages || boardYear === undefined) return;
    if (pending) {
      // Refused, not queued: two moves in flight cannot share one pending slot, and a
      // queue would let a confirmation appear for a card the user has stopped looking at.
      toast.info(t("board.oneMoveAtATime"));
      return;
    }

    const intent = classifyDrop(card, target, stages);
    if (intent.kind === "none") return;

    setPending({ card, intent });
    if (needsDialog(intent)) return;

    /**
     * Everything a drop states completely now commits without asking (2026-08-25,
     * specs/drag-and-activity.md).
     *
     * A cross-quarter drop used to open a date prompt on *every* drag — asking for a fact the
     * drop had already stated, in a meeting where the board is dragged dozens of times. The
     * quarter's last day is what the column claims ("closing by the end of Q2"), and the exact
     * date stays editable in the detail. Closure and reopening still confirm: those need an
     * *actual* close date, which a drop does not state, and the book requires the confirmation.
     */
    if (intent.kind === "stage") {
      void commit(
        () => moveStage.mutateAsync({ id: card.id, stageId: intent.stageId, version: card.version }),
        { stageId: intent.stageId, card },
      );
      return;
    }

    if (intent.kind === "quarter") {
      void commit(
        () =>
          moveQuarter.mutateAsync({
            id: card.id,
            expectedCloseDate: lastDayOfQuarter(intent.quarter, boardYear),
            version: card.version,
          }),
        { quarter: intent.quarter, card },
      );
      return;
    }

    if (intent.kind === "stage-and-quarter") {
      // One request, not two: `moveStage` carries the date as well, so the stage change and
      // the quarter change are one version bump and one coherent burst of audit events.
      void commit(
        () =>
          moveStage.mutateAsync({
            id: card.id,
            stageId: intent.stageId,
            expectedCloseDate: lastDayOfQuarter(intent.quarter, boardYear),
            version: card.version,
          }),
        { stageId: intent.stageId, quarter: intent.quarter, card },
      );
    }
  };

  const stageName = (id: string): string => stages?.find((stage) => stage.id === id)?.name ?? "";

  /**
   * "«Data warehouse assessment» moved to Validation, Q2 2026" — the record, then where it
   * landed, naming only the parts the move actually changed.
   */
  const landedMessage = ({ card, stageId, quarter }: { card: Movable; stageId?: string; quarter?: number }): string => {
    const parts: string[] = [];
    if (stageId !== undefined) parts.push(stageName(stageId));
    if (quarter !== undefined && boardYear !== undefined) parts.push(formatQuarter(quarter, boardYear));
    return t("board.moved", card.name, parts.join(", "));
  };

  const dialogs = (
    <>
      {/*
       * No dialog for `quarter` or `stage-and-quarter`.
       *
       * Both used to raise the date prompt; since 2026-08-25 (specs/drag-and-activity.md)
       * they commit straight away, taking the target quarter's last day. The prompt's blocks
       * survived that change and kept rendering — `begin` sets `pending` for *every* intent,
       * because the board draws the card at its target cell from it — so a cross-quarter drag
       * flashed a dialog asking for the fact it had just committed, and submitting it sent a
       * second write with a version the first had already spent.
       *
       * `needsDialog` is the single answer to "does this intent stop and ask": only the two
       * below are in it, and only the two below appear here.
       */}
      {pending?.intent.kind === "close" && (
        <ClosureDialog
          card={pending.card}
          closedType={pending.intent.closedType}
          pending={committing}
          onCancel={clear}
          onConfirm={(actualCloseDate, lostReason) =>
            void commit(() =>
              close.mutateAsync({
                id: pending.card.id,
                stageId: pending.intent.kind === "close" ? pending.intent.stageId : "",
                actualCloseDate,
                // Present only when losing — the dialog withholds it otherwise, and the API
                // refuses a reason on a won closure.
                lostReason,
                version: pending.card.version,
              }),
            )
          }
        />
      )}

      {pending?.intent.kind === "reopen" && boardYear !== undefined && (
        <ReopenDialog
          card={pending.card}
          targetStageName={stageName(pending.intent.stageId)}
          // The drop column is honoured: reopening into a different quarter asks for the
          // expected close date the quarter follows from, exactly as a cross-quarter move
          // does. Reopening into the card's own quarter asks for nothing.
          targetQuarter={pending.intent.quarter === pending.card.quarter ? undefined : pending.intent.quarter}
          boardYear={boardYear}
          pending={committing}
          onCancel={clear}
          onConfirm={(expectedCloseDate) =>
            void commit(() =>
              reopen.mutateAsync({
                id: pending.card.id,
                stageId: pending.intent.kind === "reopen" ? pending.intent.stageId : "",
                ...(expectedCloseDate ? { expectedCloseDate } : {}),
                version: pending.card.version,
              }),
            )
          }
        />
      )}
    </>
  );

  return {
    /** Route a drop, or a stage chosen in the detail's inline select. */
    begin,
    /** The move under way, so the board can draw its card where it is going. */
    pending,
    committing,
    dialogs,
  };
}

/**
 * Where a pending card should be drawn: its target stage and quarter.
 *
 * Presentation only. It moves one card in the rendering of a cache that has not changed —
 * which is why counts, totals and every other card stay exactly as the server last reported
 * them while a move is in flight.
 */
export function pendingPlacement(pending: PendingMove | undefined): { id: string; stageId: string; quarter: number } | undefined {
  if (!pending) return undefined;
  const { card, intent } = pending;

  switch (intent.kind) {
    case "stage":
      return { id: card.id, stageId: intent.stageId, quarter: card.quarter };
    case "quarter":
      return { id: card.id, stageId: card.stageId, quarter: intent.quarter };
    case "stage-and-quarter":
    case "close":
    case "reopen":
      return { id: card.id, stageId: intent.stageId, quarter: intent.quarter };
    default:
      return undefined;
  }
}
